import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, LogIn, Lock, User } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { fetchSpokes } from "@/lib/api";

const BASE_SPOKES = [
  { name: "Aylesbury",  username: "aylesbury",  tag: "AYL", color: "#DF513B", fg: "#FFFFFF" },
  { name: "Whitehouse", username: "whitehouse", tag: "WHS", color: "#2D5C8F", fg: "#FFFFFF" },
  { name: "Bletchley",  username: "bletchley",  tag: "BLT", color: "#4F7355", fg: "#FFFFFF" },
  { name: "Glebefarm",  username: "glebefarm",  tag: "GLF", color: "#E8B241", fg: "#1A1714" },
  { name: "Hatfield",   username: "hatfield",   tag: "HAT", color: "#7E3C8A", fg: "#FFFFFF" },
  { name: "Uxbridge",   username: "uxbridge",   tag: "UXB", color: "#C44A6A", fg: "#FFFFFF" },
];

const Marquee = () => {
  const words = ["DUM ON THE RUN", "DOTR", "BIRYANI", "RUN ON TIME", "12:45 PM", "4:30 PM"];
  // Three copies for seamless infinite scroll
  const row = [...words, ...words, ...words];
  return (
    <div className="overflow-hidden border-y-2 border-dotr-ink bg-dotr-primary text-white">
      <div className="flex gap-12 py-2 whitespace-nowrap animate-[marquee_28s_linear_infinite] font-heading uppercase tracking-[0.18em] text-sm">
        {row.map((w, i) => (
          <span key={i} className="flex items-center gap-12">
            {w}
            <span aria-hidden="true">★</span>
          </span>
        ))}
      </div>
    </div>
  );
};

export const LoginPage = () => {
  const { login } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [spokes, setSpokes] = useState(BASE_SPOKES);

  // Dynamic spoke tiles — includes admin-added franchises & their brand colours
  useEffect(() => {
    fetchSpokes()
      .then((d) => {
        if (Array.isArray(d?.spokes) && d.spokes.length) setSpokes(d.spokes);
      })
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password) {
      return toast.error("Username and password required.");
    }
    setSubmitting(true);
    try {
      const u = await login(username.trim().toLowerCase(), password);
      toast.success(`Welcome, ${u.display_name}.`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Login failed.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const pickSpoke = (s) => {
    setUsername(s.username);
    // focus password
    setTimeout(() => document.querySelector('[data-testid="input-password"]')?.focus(), 0);
  };

  return (
    <div className="min-h-screen bg-dotr-bg text-dotr-ink flex flex-col" data-testid="login-page">
      {/* Top marquee */}
      <Marquee />

      {/* Main */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 max-w-7xl w-full mx-auto px-6 md:px-10 lg:px-14 py-8 lg:py-14 gap-8 lg:gap-12">
        {/* Left: brand */}
        <div className="lg:col-span-7 flex flex-col gap-6 animate-fade-up">
          <div className="flex items-center gap-4">
            <img
              src="/dotr-logo.png"
              alt="Dum on the Run"
              className="w-16 h-16 md:w-20 md:h-20 object-contain border-2 border-dotr-ink bg-white"
              data-testid="login-brand-logo"
            />
            <div>
              <div className="font-heading font-semibold text-xl tracking-tight">DUM ON THE RUN<sup className="text-xs ml-0.5">®</sup></div>
              <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-dotr-inkmuted">
                Order Operations · DOTR
              </div>
            </div>
          </div>

          <div className="mt-4">
            <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-dotr-inkmuted mb-2">
              <span className="inline-flex items-center gap-2">
                <span className="w-2 h-2 bg-dotr-primary inline-block" />
                Login · Spoke Console · v1.0
              </span>
            </div>
            <h1 className="font-heading font-semibold text-5xl md:text-6xl lg:text-7xl uppercase tracking-tight leading-[0.92]">
              Log in.<br />
              <span className="text-dotr-primary">Log it.</span><br />
              Run it.
            </h1>
            <p className="font-body text-base text-dotr-inkmuted mt-5 max-w-lg">
              Sign in with your spoke credentials to capture today's orders, manage the run, and print receipts for the kitchen.
            </p>
          </div>

          {/* Spoke quick-pick chips */}
          <div className="mt-2">
            <div className="label-uppercase mb-2">Pick your spoke</div>
            <div className="flex flex-wrap gap-2" data-testid="spoke-chips">
              {spokes.map((s) => {
                const active = username === s.username;
                return (
                  <button
                    key={s.username}
                    type="button"
                    onClick={() => pickSpoke(s)}
                    className={`group inline-flex items-center gap-2 px-3 py-2 border-2 border-dotr-ink transition-all ${
                      active
                        ? "bg-dotr-ink text-white shadow-brutalSm translate-x-[2px] translate-y-[2px]"
                        : "bg-white text-dotr-ink shadow-brutalSm hover:bg-dotr-muted"
                    }`}
                    data-testid={`spoke-chip-${s.username}`}
                  >
                    <span
                      className="font-mono text-[10px] px-1.5 py-0.5"
                      style={{ background: active ? "#DF513B" : (s.color || "#1A1714"), color: active ? "#FFFFFF" : (s.fg || "#FFFFFF") }}
                    >
                      {s.tag}
                    </span>
                    <span className="font-heading font-semibold uppercase tracking-wider text-sm">
                      {s.name}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Run times card */}
          <div className="mt-6 brutal-card receipt-bg p-5 max-w-md hidden lg:block">
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-dotr-inkmuted mb-2">
              Today's Spoke Run
            </div>
            <div className="grid grid-cols-2 gap-4 font-mono text-sm">
              <div>
                <div className="font-heading italic text-lg">Lunch</div>
                <div className="font-bold text-2xl">12:45 PM</div>
              </div>
              <div>
                <div className="font-heading italic text-lg">Dinner</div>
                <div className="font-bold text-2xl">4:30 PM</div>
              </div>
            </div>
          </div>
        </div>

        {/* Right: login card */}
        <div className="lg:col-span-5 flex items-start lg:items-center">
          <form
            onSubmit={submit}
            className="brutal-card w-full p-7 md:p-8 animate-fade-up"
            data-testid="login-form"
          >
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-dotr-inkmuted mb-1">
              Sign in
            </div>
            <h2 className="font-heading font-semibold text-3xl tracking-tight uppercase leading-[0.95] mb-5">
              Spoke<br />Console Access
            </h2>

            <div className="flex flex-col gap-1.5 mb-4">
              <label htmlFor="login-username" className="label-uppercase flex items-center gap-1">
                <span className="text-dotr-primary">*</span> Username
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-dotr-inkmuted">
                  <User size={14} />
                </span>
                <input
                  id="login-username"
                  data-testid="input-username"
                  autoComplete="username"
                  autoCapitalize="none"
                  spellCheck={false}
                  className="brutal-input h-12 pl-9 pr-3 font-body text-base w-full"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="aylesbury"
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5 mb-5">
              <label htmlFor="login-password" className="label-uppercase flex items-center gap-1">
                <span className="text-dotr-primary">*</span> Password
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-dotr-inkmuted">
                  <Lock size={14} />
                </span>
                <input
                  id="login-password"
                  data-testid="input-password"
                  type="password"
                  autoComplete="current-password"
                  className="brutal-input h-12 pl-9 pr-3 font-body text-base w-full"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="btn-login-submit"
              className="brutal-btn w-full px-6 py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-70"
            >
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <LogIn size={16} />}
              {submitting ? "Signing in…" : "Sign in →"}
            </button>

            <div className="mt-5 pt-5 border-t-2 border-dotr-ink">
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-dotr-inkmuted mb-1">
                Internal use only
              </div>
              <p className="font-mono text-[11px] text-dotr-inkmuted leading-relaxed">
                One account per spoke. Lost your credentials? Ping ops on the group chat.
              </p>
            </div>
          </form>
        </div>
      </div>

      {/* Bottom marquee */}
      <Marquee />

      <style>{`
        @keyframes marquee {
          from { transform: translateX(0); }
          to { transform: translateX(-33.33%); }
        }
      `}</style>
    </div>
  );
};
